import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center" data-testid="contact-success">
        <Card className="glass-effect-dark max-w-md">
          <CardContent className="p-8 text-center">
            <div className="text-6xl mb-4">✉️</div>
            <h2 className="text-2xl font-bold text-orange-400 mb-4">Message Sent!</h2>
            <p className="text-gray-300 mb-6">
              Thank you for contacting us. We'll get back to you within 24-48 hours.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Back to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="contact-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-md border-b border-gray-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-gray-300 hover:text-white hover:bg-gray-800"
              data-testid="back-home-btn"
            >
              ← Back to Home
            </Button>
            <div className="flex items-center space-x-3">
              <div className="text-2xl font-bold text-white">
                <span className="text-orange-500">X</span>videxa
              </div>
              <div className="text-orange-400 text-sm bg-orange-600/20 px-2 py-1 rounded border border-orange-500/50">
                Premium HD
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 max-w-2xl">
        <div className="animate-slideInUp">
          <Card className="glass-effect-dark">
            <CardHeader>
              <CardTitle className="text-3xl font-bold title-primary text-center mb-4">
                Contact Us
              </CardTitle>
              <div className="text-center text-gray-400">
                Get in touch with the Megavideosee team
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-white mb-2 block">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-white mb-2 block">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject" className="text-white mb-2 block">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="What's this about?"
                    data-testid="contact-subject-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-white mb-2 block">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-input min-h-[120px] resize-none"
                    placeholder="Tell us what's on your mind..."
                    data-testid="contact-message-input"
                  />
                </div>

                <div className="bg-orange-600/10 border border-orange-500/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="text-orange-400 text-xl">💡</div>
                    <div className="text-sm text-gray-300">
                      <p className="font-medium text-orange-400 mb-1">Quick Response Times</p>
                      <p>
                        We typically respond to inquiries within 24-48 hours during business days. 
                        For urgent matters, please include "URGENT" in your subject line.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary hover-lift text-lg py-3"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-pulse">Sending...</div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>Send Message</span>
                      <span>📨</span>
                    </div>
                  )}
                </Button>
              </form>

              {/* Contact Information */}
              <div className="mt-12 pt-8 border-t border-gray-700">
                <h3 className="text-xl font-semibold text-orange-400 mb-6 text-center">Other Ways to Reach Us</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl mb-2">📧</div>
                    <h4 className="font-medium text-white mb-1">General Inquiries</h4>
                    <p className="text-gray-400 text-sm">support@megavideosee.com</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-2">⚖️</div>
                    <h4 className="font-medium text-white mb-1">Legal & DMCA</h4>
                    <p className="text-gray-400 text-sm">dmca@megavideosee.com</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;